import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { borderGreyFocused, dimGrey } from '../../../../../../mainTheme';

export const useCalendarStyles = () => {
  const theme = useTheme();
  const TIME_GUTTER_WIDTH = 72;

  const calendarStyle = useMemo(
    () => ({
      height: 'calc(100vh - 300px)',
      minHeight: 500,
      mt: 0,
      '& .rbc-calendar': {
        background: theme.palette.background.paper,
        borderRadius: '16px',
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)',
        border: `1px solid ${borderGreyFocused}`,
        overflow: 'hidden',
      },
      // Make header cells non-clickable
      '& .rbc-header': {
        pointerEvents: 'none',
        cursor: 'default',
        userSelect: 'none',
        height: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      // Remove default today column highlight
      '& .rbc-today': {
        backgroundColor: 'transparent !important',
      },
      // Time gutter width and style
      '& .rbc-time-gutter': {
        width: `${TIME_GUTTER_WIDTH}px !important`,
        minWidth: `${TIME_GUTTER_WIDTH}px !important`,
        maxWidth: `${TIME_GUTTER_WIDTH}px !important`,
        background: 'transparent',
        borderRight: '1px solid #F6F6F6',
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
      },
      // Time gutter header (top left cell)
      '& .rbc-time-header-gutter': {
        borderBottom: `1px solid ${borderGreyFocused}`,
        boxSizing: 'border-box',
      },
      // Time label cell (Figma typography and centering)
      '& .rbc-time-gutter .rbc-label': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        padding: 0,
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.2px',
        color: dimGrey,
        background: 'transparent',
        boxSizing: 'border-box',
      },
      // Time gutter row alignment
      '& .rbc-time-gutter .rbc-timeslot-group': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '48px',
        minHeight: '48px',
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
        background: 'transparent',
      },
      // Main grid row alignment (match gutter exactly)
      '& .rbc-timeslot-group': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '48px',
        minHeight: '48px',
        boxSizing: 'border-box',
        padding: 0,
        marginTop: 0,
        background: 'transparent',
      },
      // Event styling: fill the 15-min slot, no padding so the card is full height
      '& .rbc-event, & .rbc-event-content, & .rbc-day-slot .rbc-event': {
        backgroundColor: 'transparent !important',
        border: 'none !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        minHeight: 0,
        zIndex: 2,
        boxSizing: 'border-box',
      },
      // Empty time-range label would otherwise take height off the card
      '& .rbc-event-label': {
        display: 'none',
      },
      // Use the full day column so side-by-side events get all the width
      '& .rbc-day-slot .rbc-events-container': {
        marginRight: 0,
      },
      '& .rbc-time-content .rbc-current-time-indicator': {
        height: '2px',
        background: '#000',

        border: 'none',
        zIndex: 0,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.12)',
        width: '100%',
        left: 0,
        right: 0,
        position: 'absolute',
      },
      // Calendar grid top border
      '& .rbc-time-content': {
        borderTop: 'none',
        position: 'relative',
      },
      // Hide all-day row and cells to remove empty row below day header
      '& .rbc-allday-row, & .rbc-allday-cell': {
        display: 'none !important',
        height: 0,
        minHeight: 0,
        padding: 0,
        border: 'none',
      },
    }),
    [theme],
  );

  const formats = useMemo(
    () => ({
      timeGutterFormat: 'h:mm A',
      eventTimeRangeFormat: () => '', // Hide default event time text
      dayFormat: (date: Date) => moment(date).format('D'),
    }),
    [],
  );

  return { calendarStyle, formats };
};
